import React from "react";
import { Box, Typography } from "@mui/material";

// Two decimals and nothing else, as the Motor Calculation page prints its figures. A figure that is not
// known (null) is a dash, never 0.00.
const amount = (n) => (n === null || n === undefined ? "—" : Number(n).toFixed(2));

const SIDE = {
  od: { color: "#1E3A5F", tint: "#EEF3F9" },
  tp: { color: "#9A3412", tint: "#FDF1EA" },
};

/**
 * One insurer's side of a policy sold by two: OD on the left, TP on the right.
 *
 *   header      the side and its insurer
 *   vehicle     the vehicle that insurer is sold (name, variant, CC, IDV range)
 *   children    the fields that belong to THIS side (IDV and discount for OD, TPPD for TP)
 *   total       once calculated: this side's total, in the Motor Calculation page's words —
 *               "Total A [OD Premium]" on the left, "Total B [TP Premium]" on the right
 *
 * `total` is { label, amount, note } from utils/vehicleSelection premiumTotals, i.e. the engine's
 * own Total A / Total B. Net Premium [A + B], GST and the Final Premium follow under both panels
 * (PremiumFooter), and every row behind the totals is in View Premium Breakup.
 */
export const CompanyPanel = ({ sideKey, title, company, vehicle, idvRange, total, children }) => {
  const theme = SIDE[sideKey];
  return (
    <Box
      data-testid={`company-panel-${sideKey}`}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "1px solid #EAECF0",
        borderRadius: "10px",
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          background: theme.color,
          color: "#fff",
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.2px" }}>{title}</Typography>
        {company ? (
          <Box
            component="span"
            sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", px: 1, py: "2px", borderRadius: "4px", background: "rgba(255,255,255,0.18)" }}
          >
            {company}
          </Box>
        ) : null}
      </Box>

      <Box sx={{ px: 2, py: 1.5, background: theme.tint, borderBottom: "1px solid #EAECF0" }}>
        {vehicle ? (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#101828" }}>
              {vehicle.name}
              {vehicle.variant ? ` · ${vehicle.variant}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#475467" }}>
              {vehicle.cc ? `${vehicle.cc} CC` : "CC not set"}
              {idvRange ? ` · IDV ${idvRange}` : ""}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: "#98A2B3" }}>Vehicle not chosen yet</Typography>
        )}
      </Box>

      {children ? (
        <Box sx={{ px: 2, py: 2, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>{children}</Box>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}

      {total ? (
        <Box
          data-testid={`company-total-${sideKey}`}
          sx={{ px: 2, py: 1.25, borderTop: "1px dashed #D0D5DD", background: "#FCFCFD" }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: theme.color }}>
              {total.label}
            </Typography>
            <Typography component="span" sx={{ fontSize: 15, fontWeight: 700, color: "#101828", whiteSpace: "nowrap" }}>
              {amount(total.amount)}
            </Typography>
          </Box>
          {total.note ? <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.25 }}>{total.note}</Typography> : null}
        </Box>
      ) : null}
    </Box>
  );
};
